document.addEventListener('DOMContentLoaded', () => {
  if (!customElements.get('slide-show')) {
    class Slideshow extends HTMLElement {
      constructor() {
        super();
        this.swiperPrev = null;
        this.swiperNext = null;
        this.swiperPagination = null;
        this.swiperFraction = null;
        this.swiperFractionCurrent = null;
        this.swiperContainer = null;
        this.swiperScrollbar = null;
        this.slideshowSwiper = null;
        this.autoplayEnabled = false;
        this.autoplayDelay = 3000;
        this.hasYoutubeSlides = false;
        this.hasVideoSlides = false;
        this.autoplayTimer = null;
        this._activeVideoCleanup = null;
      }

      connectedCallback() {
        this.swiperPrev = this.querySelector('.swiper-button-prev');
        this.swiperNext = this.querySelector('.swiper-button-next');
        this.swiperPagination = this.querySelector('.swiper-pagination');
        this.swiperFraction = this.querySelector('.swiper-fraction');
        this.swiperFractionCurrent = this.querySelector('.swiper-fraction__current');
        this.swiperContainer = this.querySelector('.swiper-container');
        this.swiperScrollbar = this.querySelector('.swiper-scrollbar');
        this.slideItems = this.querySelectorAll('.swiper-slide');
        this.autoplayEnabled = this.getAttribute('data-autoplay') === 'true';
        this.autoplayDelay =
          Number(this.getAttribute('data-autoplay-delay')) || 3000;
        this.hasYoutubeSlides = !!this.querySelector('[data-plyr-provider]');
        this.hasVideoSlides = !!this.querySelector(
          '[data-html5-video], [data-plyr-embed-id]'
        );
        this.preloadSlideVideos();

        this.addEventListener('slide-plyr-ready', () => {
          if (!this.autoplayEnabled) return;

          const activeSlide = this.getActiveSlide();
          if (
            activeSlide?.querySelector('[data-plyr-embed-id]') &&
            this.getPlyrPlayer(activeSlide)
          ) {
            this.scheduleAutoplay();
          }
        });

        if (this.swiperContainer) {
          this.initSwiper();
        } else {
          this.handleSlideMedia();
        }

        if (this.slideItems.length > 0) {
          this.slideClickHandler();
        }
      }

      disconnectedCallback() {
        this.clearAutoplaySchedule();
        if (this.slideshowSwiper) {
          this.slideshowSwiper.destroy(true, true);
          this.slideshowSwiper = null;
        }
      }

      preloadSlideVideos() {
        this.querySelectorAll('.video-box video').forEach((video) => {
          video.preload = 'auto';
          video.addEventListener(
            'loadeddata',
            () => {
              if (video.currentTime === 0 && Number.isFinite(video.duration)) {
                video.currentTime = Math.min(0.001, video.duration);
              }
            },
            { once: true }
          );

          if (video.readyState === HTMLMediaElement.HAVE_NOTHING) {
            video.load();
          }
        });
      }

      isVideoVisible(video) {
        return window.getComputedStyle(video).display !== 'none';
      }

      initSwiper() {
        if (typeof Swiper === 'undefined') {
          console.error('Swiper is not defined.');
          return;
        }

        const loop = this.getAttribute('data-loop') === 'true';
        const useCustomAutoplay =
          this.autoplayEnabled && this.hasVideoSlides;

        const swiperOptions = {
          slidesPerView: 1,
          spaceBetween: 0,
          loop,
          observer: !this.hasYoutubeSlides,
          observeParents: !this.hasYoutubeSlides,
          navigation: {
            nextEl: this.swiperPrev,
            prevEl: this.swiperNext,
          },
          pagination: {
            el: this.swiperPagination,
            clickable: true,
          },
          scrollbar: {
            el: this.swiperScrollbar,
          },
          on: {
            init: () => {
              requestAnimationFrame(() => {
                this.handleSlideMedia();
                this.ensureAutoplay();
                this.updateFraction();
              });
            },
            slideChange: () => {
              this.updateFraction();
            },
            slideChangeTransitionStart: () => {
              this.clearAutoplaySchedule();
            },
            slideChangeTransitionEnd: () => {
              this.handleSlideMedia();
              this.ensureAutoplay();
            },
            reachEnd: () => {
              if (
                !this.autoplayEnabled ||
                this.getAttribute('data-loop') === 'true' ||
                this.hasVideoSlides
              ) {
                return;
              }

              this.slideshowSwiper.slideTo(0);
            },
          },
        };

        swiperOptions.navigation = {
          nextEl: this.swiperNext,
          prevEl: this.swiperPrev,
        };

        if (this.autoplayEnabled && !useCustomAutoplay) {
          swiperOptions.autoplay = {
            enabled: true,
            delay: this.autoplayDelay,
            disableOnInteraction: false,
            waitForTransition: false,
            stopOnLastSlide: false,
          };
        }

        this.slideshowSwiper = new Swiper(this.swiperContainer, swiperOptions);
      }

      clearAutoplaySchedule() {
        if (this.autoplayTimer) {
          clearTimeout(this.autoplayTimer);
          this.autoplayTimer = null;
        }

        if (this._activeVideoCleanup) {
          this._activeVideoCleanup();
          this._activeVideoCleanup = null;
        }
      }

      ensureAutoplay() {
        if (!this.autoplayEnabled || !this.slideshowSwiper) return;

        const useCustomAutoplay = this.hasVideoSlides;

        if (useCustomAutoplay) {
          this.slideshowSwiper.autoplay?.stop();
          this.scheduleAutoplay();
          return;
        }

        if (!this.slideshowSwiper.autoplay) return;

        const { autoplay } = this.slideshowSwiper;

        if (!autoplay.running) {
          autoplay.start();
          return;
        }

        if (autoplay.paused) {
          autoplay.paused = false;
          autoplay.run();
        }
      }

      scheduleAutoplay() {
        if (!this.autoplayEnabled || !this.slideshowSwiper) return;

        this.clearAutoplaySchedule();

        const activeSlide = this.getActiveSlide();
        if (!activeSlide) return;

        const html5Video = this.getActiveHtml5Video(activeSlide);
        const plyrPlayer = this.getPlyrPlayer(activeSlide);

        if (html5Video) {
          html5Video.loop = false;

          const onEnded = () => {
            html5Video.removeEventListener('ended', onEnded);
            this.advanceSlide();
          };

          html5Video.addEventListener('ended', onEnded);
          this._activeVideoCleanup = () => {
            html5Video.removeEventListener('ended', onEnded);
          };

          html5Video.currentTime = 0;
          html5Video.play().catch(() => {
            this.autoplayTimer = setTimeout(
              () => this.advanceSlide(),
              this.autoplayDelay
            );
          });
          return;
        }

        if (activeSlide.querySelector('[data-plyr-embed-id]')) {
          if (!plyrPlayer) {
            this.autoplayTimer = setTimeout(() => {
              if (!this.getPlyrPlayer(activeSlide)) {
                this.advanceSlide();
              }
            }, this.autoplayDelay);
            return;
          }

          plyrPlayer.loop = false;

          const onEnded = () => this.advanceSlide();
          plyrPlayer.on('ended', onEnded);
          this._activeVideoCleanup = () => {
            plyrPlayer.off('ended', onEnded);
          };

          if (plyrPlayer.playing) {
            plyrPlayer.restart();
          } else {
            plyrPlayer.play();
          }
          return;
        }

        this.autoplayTimer = setTimeout(
          () => this.advanceSlide(),
          this.autoplayDelay
        );
      }

      advanceSlide() {
        if (!this.slideshowSwiper) return;

        const loop = this.getAttribute('data-loop') === 'true';

        if (this.slideshowSwiper.isEnd && !loop) {
          if (this.autoplayEnabled) {
            this.slideshowSwiper.slideTo(0);
            return;
          }

          this.clearAutoplaySchedule();
          return;
        }

        this.slideshowSwiper.slideNext();
      }

      getPlyrPlayer(slide) {
        const plyrEl = slide?.querySelector('[data-plyr-embed-id]');
        if (!plyrEl) return null;
        return plyrEl.plyrPlayer || plyrEl.plyr || null;
      }

      getActiveHtml5Video(activeSlide) {
        const videos = activeSlide.querySelectorAll('[data-html5-video]');

        for (const video of videos) {
          if (this.isVideoVisible(video)) {
            return video;
          }
        }

        return null;
      }

      handleSlideMedia() {
        const activeSlide = this.getActiveSlide();

        this.querySelectorAll('[data-html5-video]').forEach((video) => {
          if (!activeSlide?.contains(video)) {
            video.pause();
          }
        });

        this.querySelectorAll('[data-plyr-embed-id]').forEach((plyrEl) => {
          const player = plyrEl.plyrPlayer || plyrEl.plyr;
          if (!player) return;

          if (!activeSlide?.contains(plyrEl)) {
            player.pause();
          }
        });

        if (!this.autoplayEnabled) {
          activeSlide
            ?.querySelectorAll('[data-html5-video]')
            .forEach((video) => {
              if (this.isVideoVisible(video)) {
                video.play().catch(() => {});
              } else {
                video.pause();
              }
            });

          const activePlayer = this.getPlyrPlayer(activeSlide);
          activePlayer?.play();
        }
      }

      updateFraction() {
        if (!this.swiperFractionCurrent || !this.slideshowSwiper) return;

        this.swiperFractionCurrent.textContent = String(
          (this.slideshowSwiper.realIndex || 0) + 1
        );
      }

      getActiveSlide() {
        if (this.slideshowSwiper) {
          return this.querySelector('.swiper-slide-active');
        }

        return this.querySelector('.new-slideshow-item');
      }

      slideClickHandler() {
        this.slideItems.forEach((item) => {
          item.addEventListener('click', (event) => {
            if (event.target.closest('a, button, .plyr, video, iframe')) return;

            const link = item.dataset.link;
            if (link) {
              window.location.href = link;
            }
          });
        });
      }
    }

    customElements.define('slide-show', Slideshow);
  }

  const slideshowElement = document.querySelector('.new-slideshow .swiper-wrapper');
  if (slideshowElement && slideshowElement.dataset.height === 'true') {
    const updateMaxHeight = () => {
      const ratio = window.innerWidth > 849 ? 2.5 : 0.67;
      slideshowElement.style.maxHeight = `${window.innerWidth / ratio}px`;
    };

    updateMaxHeight();

    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateMaxHeight, 200);
    });
  }
});
